import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  DeleteOutlined,
  MoreVertOutlined,
  VerticalAlignBottomOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import { type FC, useRef, useState } from "react"

import type { SignedUrl } from "@/graphql/codegen/graphql"
import { useFileDownload } from "@/hooks/useFileDownload"

interface FileRowActionsProps {
  row: CellContext<SignedUrl, unknown>["row"]
  canDownload?: boolean
  canDelete?: boolean
  onDelete?: (file: SignedUrl) => void
}

export const FileRowActions: FC<FileRowActionsProps> = ({ row, canDownload = true, canDelete = true, onDelete }) => {
  const { downloadFile } = useFileDownload()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleMenuOpen = () => setIsMenuOpen(true)

  const handleMenuClose = () => setIsMenuOpen(false)

  const handleView = (signedUrl: SignedUrl) => {
    handleMenuClose()
    window.open(signedUrl.url, "_blank")
  }

  const handleDownload = async (signedUrl: SignedUrl) => {
    handleMenuClose()
    const { url, file } = signedUrl

    if (!file) return

    await downloadFile(url, file.originalName!)
  }

  const handleDelete = (signedUrl: SignedUrl) => {
    handleMenuClose()
    onDelete?.(signedUrl)
  }

  return (
    <Box className='flex items-center justify-end' ref={anchorElRef}>
      <IconButton onClick={handleMenuOpen} data-testid='files.row-actions.menu-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='files.row-actions.menu'
      >
        <MenuItem data-testid='files.row-actions.view-menu-item' onClick={() => handleView(row.original)}>
          <ListItemIcon>
            <VisibilityOutlined />
          </ListItemIcon>
          <ListItemText primary='View Document' />
        </MenuItem>
        {canDownload && (
          <MenuItem data-testid='files.row-actions.download-menu-item' onClick={() => handleDownload(row.original)}>
            <ListItemIcon>
              <VerticalAlignBottomOutlined />
            </ListItemIcon>
            <ListItemText primary='Download Document' />
          </MenuItem>
        )}
        {canDelete && onDelete && (
          <MenuItem data-testid='files.row-actions.delete-menu-item' onClick={() => handleDelete(row.original)}>
            <ListItemIcon>
              <DeleteOutlined />
            </ListItemIcon>
            <ListItemText primary='Delete Document' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
